import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { fetchBootstrap } from '../infra/api/Bootstrap';
import { Box, CircularProgress } from '@mui/material';

const BootstrapContext = createContext(null);

export function BootstrapProvider({ children }) {
    const { tenant } = useParams();

    const [data, setData] = useState(null);
    const [etag, setEtag] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // cache por tenant (se quiser, inclua o usuário aqui para multi-login no mesmo browser)
    const cacheKey = useMemo(
        () => (tenant ? `bootstrap:${tenant}` : null),
        [tenant]
    );

    useEffect(() => {
        let cancelled = false;

        const run = async () => {
            if (!tenant) return;
            setLoading(true);
            setError('');

            // 1) servir cache imediato
            let cachedEtag = null;
            if (cacheKey) {
                try {
                    const raw = localStorage.getItem(cacheKey);
                    if (raw) {
                        const parsed = JSON.parse(raw); // { etag, payload, ts }
                        if (!cancelled && parsed?.payload)
                            setData(parsed.payload);
                        if (!cancelled && parsed?.etag) setEtag(parsed.etag);
                        cachedEtag = parsed?.etag || null;
                    }
                } catch {
                    // cache inválido → ignora
                }
            }

            // 2) revalidar no backend com If-None-Match
            try {
                const res = await fetchBootstrap({ etag: cachedEtag });
                if (cancelled) return;

                if (!res.notModified && res.data) {
                    const record = {
                        etag: res.etag,
                        payload: res.data,
                        ts: Date.now(),
                    };
                    if (cacheKey)
                        localStorage.setItem(cacheKey, JSON.stringify(record));
                    setData(res.data);
                    setEtag(res.etag || null);
                }
            } catch (e) {
                if (!cancelled)
                    setError(e?.message || 'Falha ao carregar bootstrap');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        run();
        return () => {
            cancelled = true;
        };
    }, [tenant, cacheKey]);

    const refresh = useMemo(
        // force=true ignora o ETag salvo e força um fetch completo (sem 304).
        // Necessário logo após uma mutação (ex: validar-email) — um 304
        // nesse momento esconderia o dado novo atrás do corpo vazio.
        () => async (force = false) => {
            try {
                const res = await fetchBootstrap({ etag: force ? null : etag });
                if (!res.notModified && res.data) {
                    const record = {
                        etag: res.etag,
                        payload: res.data,
                        ts: Date.now(),
                    };
                    if (cacheKey)
                        localStorage.setItem(cacheKey, JSON.stringify(record));
                    setData(res.data);
                    setEtag(res.etag || null);
                    return res.data;
                }
                // 304: nada mudou → devolve o atual
                return data;
            } catch (e) {
                setError(e?.message || 'Falha ao recarregar bootstrap');
                throw e;
            }
        },
        [etag, cacheKey, data]
    );

    const value = useMemo(
        () => ({ bootstrap: data, loading, error, refresh }),
        [data, loading, error, refresh]
    );

    // loading inicial sem cache
    if (loading && !data) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                sx={{ height: '100vh' }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <BootstrapContext.Provider value={value}>
            {children}
        </BootstrapContext.Provider>
    );
}

export function useBootstrap() {
    return useContext(BootstrapContext);
}
